import {
  Box,
  CircularProgress,
  Link,
  MenuItem,
  Paper,
  Select,
  Typography,
  useTheme,
} from '@mui/material';
import type { SelectChangeEvent } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { Link as RouterLink, type To } from 'react-router-dom';
import { DeviceStatus } from '~/components/devices/device-status/device-status';
import type { DeviceStatus__Device } from '~/components/devices/device-status/device-status.generated';
import { DeviceLogo } from '~/components/devices/logo';
import type { DeviceKind } from '~/generated/graphql';
import { useLink } from '~/hooks/link';
import type { StatusFilter } from '../lib/useOperationalData';

type DeviceAlertDevice = DeviceStatus__Device & {
  name: string;
  kind: DeviceKind;
  value: React.ReactNode;
};

export interface ThresholdOption {
  value: number;
  label: string;
}

interface BaseDeviceAlertCardProps {
  icon: React.ReactNode;
  title: string;
  subtitle: string;
  count: number;
  totalDevices: number;
  color: string;
  devices: DeviceAlertDevice[];
  viewAllLink: To;
  emptyMessage: string;
  loading: boolean;
  statusFilter?: StatusFilter;
  onStatusFilterChange?: (value: StatusFilter) => void;
}

interface WithThreshold {
  thresholdOptions: ThresholdOption[];
  selectedThreshold: number;
  onThresholdChange: (threshold: number) => void;
  thresholdLabel: string;
  thresholdSuffix?: string;
}

interface WithoutThreshold {
  thresholdOptions?: never;
  selectedThreshold?: never;
  onThresholdChange?: never;
  thresholdLabel?: never;
  thresholdSuffix?: never;
}

export type DeviceAlertCardProps = BaseDeviceAlertCardProps & (WithThreshold | WithoutThreshold);

export const DeviceAlertCard = ({
  icon,
  title,
  subtitle,
  count,
  totalDevices,
  color,
  devices,
  viewAllLink,
  emptyMessage,
  loading,
  thresholdOptions,
  selectedThreshold,
  onThresholdChange,
  thresholdLabel,
  thresholdSuffix,
  statusFilter,
  onStatusFilterChange,
}: DeviceAlertCardProps) => {
  const theme = useTheme();
  const { t } = useTranslation();
  const link = useLink();

  // Calculate the ring progress (capped at 100%)
  const progress = totalDevices > 0 ? Math.min((count / totalDevices) * 100, 100) : 0;
  const circumference = 2 * Math.PI * 22; // radius = 22
  const strokeDashoffset = circumference - (progress / 100) * circumference;

  const handleThresholdChange = (event: SelectChangeEvent<number>) => {
    onThresholdChange?.(Number(event.target.value));
  };

  return (
    <Paper
      sx={{
        p: 2,
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        borderLeft: '5px solid',
        borderColor: theme.palette.primary.main,
      }}
    >
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2, mb: 2 }}>
        {/* Circular progress ring with count */}
        <Box sx={{ position: 'relative', width: 56, height: 56, flexShrink: 0 }}>
          <svg width="56" height="56" viewBox="0 0 56 56">
            {/* Background circle */}
            <circle
              cx="28"
              cy="28"
              r="22"
              fill="none"
              stroke={theme.palette.grey[200]}
              strokeWidth="4"
            />
            {/* Progress circle */}
            <circle
              cx="28"
              cy="28"
              r="22"
              fill="none"
              stroke={color}
              strokeWidth="4"
              strokeLinecap="round"
              strokeDasharray={circumference}
              strokeDashoffset={strokeDashoffset}
              transform="rotate(-90 28 28)"
              style={{ transition: 'stroke-dashoffset 0.3s ease' }}
            />
            {/* Small dot at top */}
            {count > 0 && <circle cx="28" cy="6" r="4" fill={color} />}
          </svg>
          {/* Count in center */}
          <Box
            sx={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              textAlign: 'center',
            }}
          >
            <Typography component="p" variant="h6" fontWeight={700}>
              {count}
            </Typography>
          </Box>
        </Box>

        {/* Title and subtitle */}
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Box sx={{ color, display: 'flex', alignItems: 'center' }}>{icon}</Box>
            <Typography component="h2" variant="subtitle1" fontWeight={600}>
              {title}
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.5, flexWrap: 'wrap' }}>
            {onStatusFilterChange != null && (
              <>
                <Select
                  value={statusFilter ?? 'online'}
                  onChange={(e) => onStatusFilterChange(e.target.value as StatusFilter)}
                  inputProps={{
                    'aria-label': t('pages.dashboard.device_alerts.status_select', { title }),
                  }}
                  size="small"
                  variant="outlined"
                  sx={{
                    fontSize: '0.8125rem',
                    color: 'primary.text',
                    '& .MuiOutlinedInput-notchedOutline': {
                      borderColor: 'divider',
                    },
                    '& .MuiSelect-select': {
                      py: 0.25,
                      px: 1,
                      minHeight: 'unset',
                    },
                    '& .MuiSelect-icon': {
                      fontSize: '1rem',
                      color: 'primary.text',
                    },
                  }}
                >
                  <MenuItem value="all">{t('pages.dashboard.device_alerts.all')}</MenuItem>
                  <MenuItem value="offline">{t('pages.dashboard.device_alerts.offline')}</MenuItem>
                  <MenuItem value="online">{t('pages.dashboard.device_alerts.online')}</MenuItem>
                </Select>
                <Typography variant="body2" color="text.primary">
                  {t('pages.dashboard.device_alerts.devices')}
                </Typography>
              </>
            )}
            {thresholdOptions ? (
              <>
                {thresholdLabel && (
                  <Typography variant="body2" color="text.primary">
                    {thresholdLabel}
                  </Typography>
                )}
                <Select
                  value={selectedThreshold}
                  onChange={handleThresholdChange}
                  inputProps={{
                    'aria-label': t('pages.dashboard.device_alerts.threshold_select', { title }),
                  }}
                  size="small"
                  variant="outlined"
                  sx={{
                    fontSize: '0.8125rem',
                    color: 'primary.text',
                    '& .MuiOutlinedInput-notchedOutline': {
                      borderColor: 'divider',
                    },
                    '& .MuiSelect-select': {
                      py: 0.25,
                      px: 1,
                      minHeight: 'unset',
                    },
                    '& .MuiSelect-icon': {
                      fontSize: '1rem',
                      color: 'primary.text',
                    },
                  }}
                >
                  {thresholdOptions.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
                {thresholdSuffix && (
                  <Typography variant="body2" color="text.primary">
                    {thresholdSuffix}
                  </Typography>
                )}
              </>
            ) : (
              <Typography variant="body2" color="text.primary">
                {subtitle}
              </Typography>
            )}
          </Box>
        </Box>

        {/* View All link - top right */}
        <Link
          component={RouterLink}
          to={viewAllLink}
          sx={{
            color: count > 0 ? theme.palette.primary.text : theme.palette.text.secondary,
            textDecoration: count > 0 ? 'underline' : 'none',
            fontWeight: 500,
            fontSize: '0.875rem',
            whiteSpace: 'nowrap',
            flexShrink: 0,
            pointerEvents: count > 0 ? 'auto' : 'none',
          }}
        >
          {t('pages.dashboard.view_all')}
        </Link>
      </Box>

      {/* Device list */}
      <Box
        sx={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', flex: 1 }}>
            <CircularProgress aria-label={t('common.status.loading')} size={32} />
          </Box>
        ) : devices.length === 0 ? (
          <Typography variant="body1" color="text.secondary" textAlign="center" sx={{ py: 1 }}>
            {emptyMessage}
          </Typography>
        ) : (
          devices.map((device, index) => (
            <Link
              key={device.id}
              component={RouterLink}
              to={link(`/devices/${device.id}`)}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1.5,
                py: 1.5,
                textDecoration: 'none',
                color: 'inherit',
                borderBottom:
                  index < devices.length - 1 ? `1px solid ${theme.palette.divider}` : 'none',
                '&:hover': {
                  backgroundColor: theme.palette.grey[50],
                },
              }}
            >
              {/* Status icon */}
              <Box sx={{ flexShrink: 0, display: 'flex', alignItems: 'center' }}>
                <DeviceStatus device={device} />
              </Box>

              {/* Kind icon + Device name */}
              <Box sx={{ flex: 1, minWidth: 0, display: 'flex', alignItems: 'center', gap: 1 }}>
                <DeviceLogo kind={device.kind} />
                <Typography variant="body1" fontWeight={500} noWrap>
                  {device.name}
                </Typography>
              </Box>

              {/* Value */}
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 0.5,
                  color,
                  flexShrink: 0,
                  fontWeight: 600,
                  fontSize: '0.875rem',
                }}
              >
                {device.value}
              </Box>
            </Link>
          ))
        )}
      </Box>
    </Paper>
  );
};
